import type { VpCamera, VpClippingPlane, VpVec3 } from '@viewer/lib/viewpoint'
import type { SectionLocalExtents, SectionOrientation } from '@viewer/store/sectionSlice'
import type { PlanoDeCorte } from '@viewer/section-box/planosDeCorte'
import { intervaloNaCaixa, type CaixaSimples } from '@viewer/bcf/lib/pinAncora'
import { lerVisualizacao, type VisualizacaoLida } from './bcfVisinfo'
import type { Referencial } from './bcfCoordenadas'

/**
 * A vista de um viewpoint importado (`.bcfv` de um ficheiro), sem
 * viewer e sem store.
 *
 * Um `.bcfv` não diz em que referencial estão as coordenadas: cada
 * plataforma escreve nas de um ficheiro IFC. Por isso a leitura tenta os
 * referenciais candidatos (o da regra comum primeiro, ver
 * `bcfReferencial.ts`) e fica com o primeiro em que a câmara cai junto
 * dos modelos abertos. Quando nenhum serve, o "Ver" mostra os elementos
 * em vez da câmara (ver `bcfVerImportado.ts`).
 */

/** Distância máxima, em metros, da câmara à caixa dos modelos abertos
 *  para a câmara do ficheiro ser aplicada. Uma vista de conjunto de um
 *  edifício grande fica a algumas centenas de metros; uma câmara lida no
 *  referencial errado fica a quilómetros (no Cha139, a cerca de 150 km). */
export const DISTANCIA_PLAUSIVEL_M = 500

/** Folga mínima, em metros, da caixa dos modelos para o teste do olhar. */
export const MARGEM_DO_OLHAR_M = 50

/** Fracção da distância à caixa que se junta à folga do olhar: de longe,
 *  uma câmara pode olhar um pouco ao lado e continuar a mostrar o
 *  edifício (cerca de 27 graus para cada lado). */
export const FRACCAO_DO_OLHAR = 0.5

/** Distância de um ponto a uma caixa (0 quando está dentro). */
export function distanciaACaixa(p: VpVec3, c: CaixaSimples): number {
  const dx = Math.max(c.min.x - p.x, 0, p.x - c.max.x)
  const dy = Math.max(c.min.y - p.y, 0, p.y - c.max.y)
  const dz = Math.max(c.min.z - p.z, 0, p.z - c.max.z)
  return Math.hypot(dx, dy, dz)
}

function finito(v: VpVec3 | undefined | null): v is VpVec3 {
  return !!v && Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z)
}

function alargada(c: CaixaSimples, m: number): CaixaSimples {
  return {
    min: { x: c.min.x - m, y: c.min.y - m, z: c.min.z - m },
    max: { x: c.max.x + m, y: c.max.y + m, z: c.max.z + m },
  }
}

/**
 * A câmara está junto dos modelos abertos e a olhar para eles?
 *
 * - A posição fica a menos de `DISTANCIA_PLAUSIVEL_M` da caixa dos
 *   modelos (dentro dela conta como zero).
 * - De dentro da caixa, serve qualquer direcção. De fora, o olhar tem de
 *   atravessar a caixa alargada de `MARGEM_DO_OLHAR_M` ou de
 *   `FRACCAO_DO_OLHAR` da distância, o que for maior.
 *
 * Sem caixa (nenhum modelo aberto com geometria) não há com que comparar:
 * false.
 */
export function camaraPlausivel(
  camera: Pick<VpCamera, 'position' | 'direction'> | null | undefined,
  caixa: CaixaSimples | null,
): boolean {
  if (!camera || !caixa) return false
  const p = camera.position
  const d = camera.direction
  if (!finito(p) || !finito(d) || Math.hypot(d.x, d.y, d.z) < 1e-12) return false
  const distancia = distanciaACaixa(p, caixa)
  if (distancia > DISTANCIA_PLAUSIVEL_M) return false
  if (distancia === 0) return true
  const margem = Math.max(MARGEM_DO_OLHAR_M, distancia * FRACCAO_DO_OLHAR)
  return intervaloNaCaixa(p, d, alargada(caixa, margem)) !== null
}

/** Os planos de corte caem junto dos modelos? Cada ponto dos planos a
 *  menos de `DISTANCIA_PLAUSIVEL_M` da caixa. Sem planos, sim. */
export function planosPlausiveis(
  planos: readonly VpClippingPlane[],
  caixa: CaixaSimples | null,
): boolean {
  if (planos.length === 0) return true
  if (!caixa) return false
  return planos.every(
    (p) => finito(p.location) && finito(p.direction) && distanciaACaixa(p.location, caixa) <= DISTANCIA_PLAUSIVEL_M,
  )
}

export interface CandidatoDeReferencial {
  referencial: Referencial | null
  /** Para o diagnóstico: "regra", "modelo <ficheiro>", "mundo". */
  origem: string
}

export interface LeituraEscolhida {
  lida: VisualizacaoLida
  referencial: Referencial | null
  origem: string
  /** A câmara existe e cai junto dos modelos abertos. */
  plausivel: boolean
  /** Os planos de corte caem junto dos modelos (sem planos, true). */
  planosPlausiveis: boolean
}

/**
 * Lê o `.bcfv` com o primeiro referencial em que a câmara é plausível.
 * Sem nenhum plausível (ou sem caixa dos modelos), fica a leitura com o
 * primeiro candidato, o da regra comum, marcada como não plausível.
 * Null quando o XML não se lê.
 */
export function escolherLeitura(
  xml: string,
  candidatos: readonly CandidatoDeReferencial[],
  caixa: CaixaSimples | null,
): LeituraEscolhida | null {
  const lista = candidatos.length > 0 ? candidatos : [{ referencial: null, origem: 'mundo' }]
  let primeira: LeituraEscolhida | null = null
  for (const c of lista) {
    const lida = lerVisualizacao(xml, c.referencial)
    if (!lida) return null
    const plausivel = camaraPlausivel(lida.camera, caixa)
    const escolha: LeituraEscolhida = {
      lida,
      referencial: c.referencial,
      origem: c.origem,
      plausivel,
      planosPlausiveis: planosPlausiveis(lida.clipping_planes, caixa),
    }
    if (plausivel) return escolha
    primeira ??= escolha
    // Sem câmara no ficheiro, nenhum referencial a torna plausível.
    if (!lida.camera) break
  }
  return primeira
}

/** Produto escalar. */
function produto(a: VpVec3, b: VpVec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z
}

function unitario(v: VpVec3): VpVec3 | null {
  const n = Math.hypot(v.x, v.y, v.z)
  if (!Number.isFinite(n) || n < 1e-12) return null
  return { x: v.x / n, y: v.y / n, z: v.z / n }
}

function vectorial(a: VpVec3, b: VpVec3): VpVec3 {
  return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }
}

const TOLERANCIA_ANGULAR = 1e-3

export interface CaixaDosPlanos {
  orientation: SectionOrientation
  extents: SectionLocalExtents
}

/**
 * Os planos de corte de um `.bcfv` como caixa de corte, quando formam
 * uma: seis planos em três pares de normais opostas, perpendiculares
 * entre pares. No BCF a direcção de cada plano aponta para o lado que se
 * corta; numa caixa (como a da exportação dos conflitos) apontam todas
 * para fora. Devolve a orientação (eixos unitários, origem na origem do
 * mundo, como a orientação identidade) e as extensões ao longo de cada
 * eixo. Quando os eixos são os do mundo, a orientação é exactamente a
 * identidade. Null quando os planos não formam uma caixa.
 */
export function caixaDosPlanos(planos: readonly VpClippingPlane[]): CaixaDosPlanos | null {
  if (planos.length !== 6) return null
  const normais = planos.map((p) => unitario(p.direction))
  if (normais.some((n) => !n)) return null
  const usados = new Set<number>()
  const pares: { eixo: VpVec3; min: number; max: number }[] = []
  for (let i = 0; i < 6; i++) {
    if (usados.has(i)) continue
    const ni = normais[i]!
    let j = -1
    for (let k = i + 1; k < 6; k++) {
      if (!usados.has(k) && produto(ni, normais[k]!) < -1 + TOLERANCIA_ANGULAR) {
        j = k
        break
      }
    }
    if (j < 0) return null
    usados.add(i)
    usados.add(j)
    // O eixo é a normal com a maior componente positiva num eixo do
    // mundo, para uma caixa alinhada dar a identidade.
    const maior = (n: VpVec3) => [n.x, n.y, n.z].reduce((a, b) => (Math.abs(b) > Math.abs(a) ? b : a))
    const [mais, menos] = maior(ni) >= 0 ? [i, j] : [j, i]
    const eixo = normais[mais]!
    const max = produto(planos[mais].location, eixo)
    const min = produto(planos[menos].location, eixo)
    if (!(max > min)) return null
    pares.push({ eixo, min, max })
  }
  for (let a = 0; a < 3; a++) {
    for (let b = a + 1; b < 3; b++) {
      if (Math.abs(produto(pares[a].eixo, pares[b].eixo)) > TOLERANCIA_ANGULAR) return null
    }
  }
  // Ordena os pares pelo eixo do mundo mais próximo (x, y, z).
  const indiceDoMundo = (e: VpVec3) => {
    const c = [Math.abs(e.x), Math.abs(e.y), Math.abs(e.z)]
    return c.indexOf(Math.max(...c))
  }
  const ordenados = [...pares].sort((p, q) => indiceDoMundo(p.eixo) - indiceDoMundo(q.eixo))
  if (new Set(ordenados.map((p) => indiceDoMundo(p.eixo))).size !== 3) return null
  const [px, py, pz] = ordenados
  // Base directa: se X × Y aponta contra Z, inverte-se Z (e o intervalo).
  let eixoZ = pz.eixo
  let minZ = pz.min
  let maxZ = pz.max
  if (produto(vectorial(px.eixo, py.eixo), eixoZ) < 0) {
    eixoZ = { x: -eixoZ.x, y: -eixoZ.y, z: -eixoZ.z }
    ;[minZ, maxZ] = [-pz.max, -pz.min]
  }
  const alinhado = (e: VpVec3, i: number) => Math.abs([e.x, e.y, e.z][i] - 1) < TOLERANCIA_ANGULAR
  const identidade = alinhado(px.eixo, 0) && alinhado(py.eixo, 1) && alinhado(eixoZ, 2)
  const v = (e: VpVec3, i: number): [number, number, number] =>
    identidade ? ([0, 1, 2].map((k) => (k === i ? 1 : 0)) as [number, number, number]) : [e.x, e.y, e.z]
  return {
    orientation: { origin: [0, 0, 0], axisX: v(px.eixo, 0), axisY: v(py.eixo, 1), axisZ: v(eixoZ, 2) },
    extents: { minX: px.min, maxX: px.max, minY: py.min, maxY: py.max, minZ, maxZ },
  }
}

/** O intervalo dos cursores de uma caixa orientada: as extensões dela
 *  unidas à projecção da caixa dos modelos nos mesmos eixos, para se
 *  poder alargar o corte até ao modelo inteiro. */
export function intervaloDaCaixa(c: CaixaDosPlanos, modelos: CaixaSimples | null): SectionLocalExtents {
  if (!modelos) return { ...c.extents }
  const cantos: VpVec3[] = []
  for (const x of [modelos.min.x, modelos.max.x]) {
    for (const y of [modelos.min.y, modelos.max.y]) {
      for (const z of [modelos.min.z, modelos.max.z]) cantos.push({ x, y, z })
    }
  }
  const eixo = (a: [number, number, number]): VpVec3 => ({ x: a[0], y: a[1], z: a[2] })
  const proj = (a: [number, number, number]) => cantos.map((p) => produto(p, eixo(a)))
  const [ex, ey, ez] = [proj(c.orientation.axisX), proj(c.orientation.axisY), proj(c.orientation.axisZ)]
  return {
    minX: Math.min(c.extents.minX, ...ex),
    maxX: Math.max(c.extents.maxX, ...ex),
    minY: Math.min(c.extents.minY, ...ey),
    maxY: Math.max(c.extents.maxY, ...ey),
    minZ: Math.min(c.extents.minZ, ...ez),
    maxZ: Math.max(c.extents.maxZ, ...ez),
  }
}

/** Prefixo do identificador dos planos soltos postos pelo "Ver" de um
 *  viewpoint importado: o "Ver" seguinte tira-os, sem tocar nos planos
 *  que a utilizadora criou. */
export const PREFIXO_PLANO_DO_FICHEIRO = 'bcf-ficheiro-'

/** Planos que não formam caixa, como planos de corte soltos. A normal de
 *  um plano solto aponta para o lado que fica; a direcção do BCF, para o
 *  lado que se corta: inverte-se. */
export function planosSoltos(planos: readonly VpClippingPlane[]): PlanoDeCorte[] {
  const out: PlanoDeCorte[] = []
  planos.forEach((p, i) => {
    const n = unitario(p.direction)
    if (!n || !finito(p.location)) return
    out.push({
      id: `${PREFIXO_PLANO_DO_FICHEIRO}${i + 1}`,
      nome: `Corte do ficheiro BCF ${i + 1}`,
      normal: [-n.x, -n.y, -n.z],
      ponto: [p.location.x, p.location.y, p.location.z],
      activo: true,
    })
  })
  return out
}
